import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { PaneHeader } from '@/components/settings/pane-header';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  fetchAppointmentConfirmationSettings,
  putAppointmentConfirmationSettings,
} from '@/lib/api/settings';

// Splits the stored templates map into the pinned "default" body and the
// per-type rows (each gets a stable local id for React keys / editing).
function splitTemplates(templates, nextId) {
  const map = templates && typeof templates === 'object' ? templates : {};
  const defaultBody = String(map.default || '');
  const typeRows = Object.entries(map)
    .filter(([key]) => key !== 'default')
    .map(([type, body]) => ({ id: nextId(), type: String(type), body: String(body) }));
  return { defaultBody, typeRows };
}

/**
 * Editor for the tenant-wide appointment-confirmation templates. The "default"
 * body is used for any appointment type without a dedicated template; per-type
 * rows override it by matching the appointment's `type`. The thank-you is the
 * bot's reply after a patient confirms. Saving is gated by settings:manage
 * (server-enforced); a non-editor sees read-only inputs.
 */
export function AppointmentConfirmationSubPage() {
  const idRef = useRef(0);
  const nextId = () => {
    idRef.current += 1;
    return `row-${idRef.current}`;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [defaultBody, setDefaultBody] = useState('');
  const [typeRows, setTypeRows] = useState([]);
  const [thankYou, setThankYou] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchAppointmentConfirmationSettings()
      .then((res) => {
        if (cancelled) return;
        const { defaultBody: db, typeRows: rows } = splitTemplates(res.templates, nextId);
        setDefaultBody(db);
        setTypeRows(rows);
        setThankYou(res.thankYou || '');
        setPlaceholders(res.placeholders || []);
        setCanEdit(Boolean(res.canEdit));
      })
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'Could not load confirmation templates');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTypeRow = () => setTypeRows((rows) => [...rows, { id: nextId(), type: '', body: '' }]);
  const updateRow = (id, patch) =>
    setTypeRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  const removeRow = (id) => setTypeRows((rows) => rows.filter((row) => row.id !== id));

  const handleSave = async () => {
    if (!canEdit || saving) return;
    // Assemble the templates map: default + each non-empty, uniquely-typed row.
    const templates = { default: defaultBody };
    const seen = new Set(['default']);
    for (const row of typeRows) {
      const type = String(row.type || '').trim();
      const body = String(row.body || '').trim();
      if (!type || !body || seen.has(type)) continue;
      seen.add(type);
      templates[type] = row.body;
    }
    setSaving(true);
    try {
      const res = await putAppointmentConfirmationSettings({ templates, thankYou });
      const { defaultBody: db, typeRows: rows } = splitTemplates(res.templates, nextId);
      setDefaultBody(db);
      setTypeRows(rows);
      setThankYou(res.thankYou || '');
      setCanEdit(Boolean(res.canEdit));
      toast.success('Appointment confirmation templates saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save templates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="Appointment confirmations"
        description="The WhatsApp confirmation staff send when they book or change an appointment. Shared with your whole team."
      />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto max-w-3xl space-y-6">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading templates…</div>
          ) : (
            <>
              {placeholders.length ? (
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-xs font-semibold text-foreground">Available placeholders</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {placeholders.map((name) => (
                      <code
                        key={name}
                        className="rounded bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]"
                      >{`{{${name}}}`}</code>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    A label line whose value is empty (e.g. a missing address) is dropped automatically.
                  </div>
                </div>
              ) : null}

              <section className="space-y-2">
                <label className="text-sm font-semibold text-foreground">Default template</label>
                <p className="text-xs text-muted-foreground">
                  Used for any appointment type without a specific template below.
                </p>
                <Textarea
                  value={defaultBody}
                  onChange={(e) => setDefaultBody(e.target.value)}
                  disabled={!canEdit}
                  rows={12}
                  className="font-mono text-sm"
                />
              </section>

              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-foreground">Per-type templates</div>
                    <p className="text-xs text-muted-foreground">
                      Override the default for a specific appointment type, or a doctor type (e.g.
                      “Optometrist”) — the appointment’s type is matched first, then the doctor’s type.
                    </p>
                  </div>
                  {canEdit ? (
                    <Button type="button" variant="outline" size="sm" onClick={addTypeRow}>
                      <Plus className="mr-1.5 size-4" aria-hidden />
                      Add type
                    </Button>
                  ) : null}
                </div>
                {typeRows.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No per-type templates yet.</p>
                ) : (
                  typeRows.map((row) => (
                    <div key={row.id} className="space-y-2 rounded-lg border border-border p-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={row.type}
                          onChange={(e) => updateRow(row.id, { type: e.target.value })}
                          disabled={!canEdit}
                          placeholder="Appointment type or doctor type (e.g. Optometrist)"
                          className="max-w-xs"
                        />
                        {canEdit ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeRow(row.id)}
                            aria-label="Remove template"
                          >
                            <Trash2 className="size-4 text-muted-foreground" aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                      <Textarea
                        value={row.body}
                        onChange={(e) => updateRow(row.id, { body: e.target.value })}
                        disabled={!canEdit}
                        rows={10}
                        className="font-mono text-sm"
                      />
                    </div>
                  ))
                )}
              </section>

              <section className="space-y-2">
                <label className="text-sm font-semibold text-foreground">Bot thank-you reply</label>
                <p className="text-xs text-muted-foreground">
                  Sent by the bot after a patient confirms their attendance.
                </p>
                <Textarea
                  value={thankYou}
                  onChange={(e) => setThankYou(e.target.value)}
                  disabled={!canEdit}
                  rows={3}
                  className="font-mono text-sm"
                />
              </section>

              {canEdit ? (
                <div className="flex justify-end">
                  <Button type="button" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save templates'}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  You don’t have permission to edit shared settings — these are read-only.
                </p>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
